import React, { useState } from 'react';
import { 
  Tabs, Typography, Button, Card, Table, Form, Select, Input, Space, Row, Col, Divider, Empty, Tag, Modal 
} from 'antd';
import { 
  SaveOutlined, CloseOutlined, PlusOutlined, DeleteOutlined, DatabaseOutlined, LinkOutlined, SettingOutlined 
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Mock Data
const mockColumns = [
  { key: '1', name: 'order_id', type: 'Integer', isMetric: false, tags: ['PK'] },
  { key: '2', name: 'customer_id', type: 'Integer', isMetric: false, tags: ['FK'] },
  { key: '3', name: 'amount', type: 'Decimal', isMetric: true, tags: [] },
  { key: '4', name: 'order_date', type: 'Date', isMetric: false, tags: ['Time'] },
];

const mockJoins = [
    { key: '1', type: 'Left', target: 'customers', on: 'orders.customer_id = customers.id' }
];

const DatasetEditorPage: React.FC = () => {
    const navigate = useNavigate();
    const { id, workspaceId } = useParams();
    const isNew = !id;

    // State for Columns
    const [columns, setColumns] = useState(mockColumns);

    // State for Joins
    const [joins, setJoins] = useState(mockJoins);

    // State for Settings
    const [settings, setSettings] = useState({
        where: 'market_code != "Mark001"',
        filters: ['order_date', 'status']
    });

    const handleSave = () => {
        // Simulate save
        console.log('Saving dataset...', { columns, joins, settings });
        navigate(`/${workspaceId}/dataset`);
    };

    // Columns Configuration
    const columnsConfig = [
        { title: 'Column Name', dataIndex: 'name', key: 'name' },
        { 
            title: 'Data Type', 
            dataIndex: 'type', 
            key: 'type',
            render: (text: string, record: any) => (
                <Select defaultValue={text} style={{ width: 120 }}>
                    <Option value="Integer">Integer</Option>
                    <Option value="String">String</Option>
                    <Option value="Date">Date</Option>
                    <Option value="Decimal">Decimal</Option>
                    <Option value="Boolean">Boolean</Option>
                </Select>
            )
        },
        {
            title: 'Type',
            key: 'isMetric',
            render: (_: any, record: any) => (
               <Select defaultValue={record.isMetric ? 'Metric' : 'Dimension'} style={{ width: 120 }}>
                   <Option value="Dimension">Dimension</Option>
                   <Option value="Metric">Metric</Option>
               </Select>
            )
        },
        {
            title: 'Tags',
            key: 'tags',
            render: (_: any, record: any) => (
                <Select mode="tags" defaultValue={record.tags} style={{ width: '100%' }} tokenSeparators={[',']}>
                    <Option value="PK">PK</Option>
                    <Option value="FK">FK</Option>
                    <Option value="Time">Time</Option>
                    <Option value="PII">PII</Option>
                </Select>
            )
        }
    ];

    // Joins Configuration
    const joinsColumns = [
        { 
            title: 'Join Type', 
            dataIndex: 'type', 
            key: 'type',
             render: (text: string) => (
                <Select defaultValue={text} style={{ width: 100 }}>
                    <Option value="Inner">Inner</Option>
                    <Option value="Left">Left</Option>
                    <Option value="Right">Right</Option>
                </Select>
            )
        },
        { title: 'Target Table', dataIndex: 'target', key: 'target', render: (text: string) => <Input defaultValue={text} /> },
        { title: 'Join Condition (ON)', dataIndex: 'on', key: 'on',  render: (text: string) => <Input defaultValue={text} /> },
        { 
            title: 'Action', 
            key: 'action',
            render: () => <Button type="text" danger icon={<DeleteOutlined />} />
        }
    ];

    return (
        <div style={{ padding: '24px', height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space direction="vertical" size={0}>
                    <Title level={3} style={{ margin: 0 }}>{isNew ? 'New Dataset' : 'Sales Orders'}</Title>
                    <Text type="secondary">Define your dataset structure and relationships</Text>
                </Space>
                <Space>
                    <Button onClick={() => navigate(`/${workspaceId}/dataset`)}>Cancel</Button>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>Save Dataset</Button>
                </Space>
            </div>

            {/* Main Content */}
            <Card style={{ flex: 1, display: 'flex', flexDirection: 'column' }} bodyStyle={{ flex: 1, padding: 0 }}>
                 <Tabs 
                    defaultActiveKey="1" 
                    tabPosition="left"
                    style={{ height: '100%' }}
                    items={[
                        {
                            key: '1',
                            label: (<span><DatabaseOutlined /> Columns</span>),
                            children: (
                                <div style={{ padding: '24px', height: '100%', overflowY: 'auto' }}>
                                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                                        <Title level={4}>Columns & Metadata</Title>
                                        <Button icon={<PlusOutlined />}>Add Column</Button>
                                    </div>
                                    <Table 
                                        columns={columnsConfig} 
                                        dataSource={columns} 
                                        pagination={false} 
                                        size="middle"
                                        bordered
                                    />
                                </div>
                            )
                        },
                        {
                             key: '2',
                            label: (<span><LinkOutlined /> Joins</span>),
                            children: (
                                <div style={{ padding: '24px' }}>
                                     <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                                        <Title level={4}>Table Joins</Title>
                                        <Button type="dashed" icon={<PlusOutlined />}>Add Join</Button>
                                    </div>
                                    <Table 
                                        columns={joinsColumns} 
                                        dataSource={joins} 
                                        pagination={false} 
                                        bordered
                                    />
                                </div>
                            )
                        },
                        {
                            key: '3',
                            label: (<span><SettingOutlined /> Settings</span>),
                            children: (
                                <div style={{ padding: '24px', maxWidth: 800 }}>
                                    <Title level={4}>Dataset Configuration</Title>
                                    <Divider />
                                    <Form layout="vertical">
                                        <Form.Item label="Global Where Condition" help="SQL condition applied to all queries on this dataset">
                                            <TextArea 
                                                rows={4} 
                                                value={settings.where} 
                                                style={{ fontFamily: 'monospace' }} 
                                            />
                                        </Form.Item>
                                        
                                        <Divider />
                                        
                                        <Form.Item label="Required Filters" help="Users must filter by these columns when querying">
                                            <Select 
                                                mode="multiple" 
                                                style={{ width: '100%' }} 
                                                placeholder="Select columns"
                                                defaultValue={settings.filters}
                                            >
                                                {columns.map(c => <Option key={c.name} value={c.name}>{c.name}</Option>)}
                                            </Select>
                                        </Form.Item>
                                    </Form>
                                </div>
                            )
                        }
                    ]}
                 />
            </Card>
        </div>
    );
};

export default DatasetEditorPage;
